let googleMapsPromise: Promise<typeof google> | null = null;

// Name of the global function Google invokes once the Maps JS API is fully loaded.
// We resolve on this callback rather than script.onload: with `loading=async`, onload
// can fire before `google.maps.importLibrary` is registered, so resolving on onload
// races and leaves `google.maps.importLibrary` undefined at call time.
const READY_CALLBACK = '__orbitGoogleMapsReady';

export function loadGoogleMaps(apiKey: string): Promise<typeof google> {
    if (googleMapsPromise) return googleMapsPromise;

    if (!apiKey) {
        return Promise.reject(new Error('Google Maps API key not configured'));
    }

    googleMapsPromise = new Promise<typeof google>((resolve, reject) => {
        if (typeof window === 'undefined') {
            googleMapsPromise = null;
            reject(new Error('Cannot load Google Maps outside a browser'));
            return;
        }

        // Already fully loaded (importLibrary registered) — e.g. a prior call or HMR.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (typeof google !== 'undefined' && typeof google.maps?.importLibrary === 'function') {
            resolve(google);
            return;
        }

        const w = window as unknown as Record<string, (() => void) | undefined>;

        // Google calls this once the API is ready and importLibrary is available.
        w[READY_CALLBACK] = () => {
            w[READY_CALLBACK] = undefined;
            resolve(google);
        };

        // `loading=async` pairs with the dynamic importLibrary() calls in searchApi.ts;
        // `callback` guarantees importLibrary exists before we use it.
        const params = new URLSearchParams({
            key: apiKey,
            v: 'weekly',
            loading: 'async',
            callback: READY_CALLBACK,
        });

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
            w[READY_CALLBACK] = undefined;
            googleMapsPromise = null;
            reject(new Error('Failed to load Google Maps script'));
        };
        document.head.appendChild(script);
    });

    return googleMapsPromise;
}
